/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHandHoldingHeart, 
  faUserFriends, 
  faAward,
  faArrowLeft,
  faBuilding,
  faBriefcase
} from '@fortawesome/free-solid-svg-icons';
import '../styles/Companies.css';
import '../styles/Volunteers.css';
import { buildApiUrl } from '../config/api';

const FEATURED_VOLUNTEERS = [
  {
    id: 'scolar-odhiambo',
    name: 'Scolar Odhiambo',
    title: 'Executive Director',
    company: 'Enact Youth Skills',
    image: '/Volunteer.png'
  },
  {
    id: 'murti-patel',
    name: 'Murti Patel',
    title: 'Motivational speaker',
    image: '/Murti-Patel.png',
    imageClass: 'volunteer-portrait-photo--murti'
  }
];

const Volunteers = () => {
  const navigate = useNavigate();
  const [volunteerStats, setVolunteerStats] = useState({
    activeVolunteers: 0,
    initiatives: 0,
    livesImpacted: 0,
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/public/stats-summary'));
        if (!res.ok) return;
        const payload = await res.json();
        if (payload?.success && payload.data) {
          setVolunteerStats((prev) => ({
            ...prev,
            activeVolunteers: payload.data.volunteers ?? 0,
          }));
        }
      } catch (e) {
        console.error('Failed to load volunteer stats', e);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="companies-page volunteers-page">
      <button 
        className="back-to-home-btn" 
        onClick={() => navigate('/')}
        aria-label="Back to Homepage"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
        <span>Back to Home</span>
      </button>

      <div className="companies-hero">
        <div className="companies-hero-overlay"></div>
        <div className="companies-hero-content">
          <div className="hero-badge">
            <FontAwesomeIcon icon={faHandHoldingHeart} className="badge-icon" />
            <span>VOLUNTEER NETWORK</span>
          </div>
          <h1 className="companies-hero-title">
            Explore Dedicated Volunteers <br />
            <span className="gradient-text">Making Impact Together</span>
          </h1>
          <p className="companies-hero-subtitle">
            Connect with passionate volunteers actively contributing to build better career pathways for all
          </p>
          <div className="companies-stats">
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faUserFriends} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{volunteerStats.activeVolunteers}+</div>
                <div className="stat-label">Active Volunteers</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faHandHoldingHeart} />
              </div>
              <div className="stat-content">
                <div className="stat-number">0+</div>
                <div className="stat-label">Initiatives</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faAward} />
              </div>
              <div className="stat-content">
                <div className="stat-number">0+</div>
                <div className="stat-label">Lives Impacted</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </div>

      <section className="volunteers-showcase">
        <div className="volunteers-showcase-header">
          <h2>Featured Volunteers</h2>
          <p>
            Meet the dedicated leaders and changemakers volunteering their time to
            strengthen career pathways across our community.
          </p>
        </div>

        <div className="volunteers-grid">
          {FEATURED_VOLUNTEERS.map((volunteer) => (
            <article key={volunteer.id} className="volunteer-profile-card">
              <div className="volunteer-card-accent" aria-hidden="true" />
              <span className="volunteer-badge">
                <FontAwesomeIcon icon={faHandHoldingHeart} />
                Volunteer
              </span>

              <div className="volunteer-portrait-frame">
                <div className="volunteer-portrait-ring">
                  <div className="volunteer-portrait-inner">
                    <img
                      src={volunteer.image}
                      alt={volunteer.name}
                      className={`volunteer-portrait-photo${volunteer.imageClass ? ` ${volunteer.imageClass}` : ''}`}
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>

              <div className="volunteer-profile-body">
                <h3 className="volunteer-name">{volunteer.name}</h3>
                <p className="volunteer-title">
                  <FontAwesomeIcon icon={faBriefcase} className="volunteer-meta-icon" />
                  {volunteer.title}
                </p>
                {volunteer.company && (
                  <p className="volunteer-company">
                    <FontAwesomeIcon icon={faBuilding} className="volunteer-meta-icon" />
                    {volunteer.company}
                  </p>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Volunteers;

